import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";
import ColorSwatch from "containers/admin/clients/client/theme-page/preview/components/color-swatch/color-swatch";

import { getGroupedAdobeFonts } from "containers/admin/clients/client/theme-page/preview/helpers/formatters-on-post-data/formatters-on-post-data";

import {
  TypographyOptions,
  GoogleFontOption,
  CustomSelectOption,
  BasicSelectOption,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import { TypekitFont } from "types";

import { FALLBACK_FONT_FAMILY_OPTIONS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/typography/typography.constants";

import Icons from "assets";

export const useTypographyOptions = ({
  currentFontWeights,
}: {
  currentFontWeights: BasicSelectOption[];
}): TypographyOptions => {
  const { colors, fonts, adobeFontsList } = useThemePreviewContext();

  const { primary, secondary, tertiary, intense, bold, soft, subtle, white } = colors!;
  const { googleFonts } = fonts!;

  //form
  const {
    formState: { errors },
    getValues,
  } = useFormContext();

  // helper for alert icon
  const getIsAlertIconShown = ({ color, colorName }: { color: string; colorName: string }) =>
    !(color !== "" && color !== "#" && !(errors?.colors && colorName in errors.colors));

  // helper to format array of all applied fonts
  const formatFontOptions = (): CustomSelectOption[] => {
    const fontOptionsMap = new Map<string, CustomSelectOption>();

    const groupedAdobeFonts = getGroupedAdobeFonts(adobeFontsList);

    // add adobe fonts
    if (adobeFontsList) {
      const adobeFontOptions = groupedAdobeFonts
        .filter(
          (font): font is TypekitFont & { name: string; slug: string } =>
            typeof font.name === "string" &&
            font.name !== "" &&
            typeof font.slug === "string" &&
            font.slug !== "",
        )
        // extract the font name from the font slug
        .map((font) => ({
          label: font.name,
          value: font.slug,
        }));

      // add adobe fonts to map using label as key to prevent duplicates
      adobeFontOptions.forEach((font) => {
        fontOptionsMap.set(font.label, font);
      });
    }

    // add google fonts
    const appliedGoogleFonts = googleFonts?.filter(
      (font): font is GoogleFontOption =>
        typeof font !== "string" && Boolean(font?.appliedToPreview),
    );

    if (appliedGoogleFonts?.length) {
      const googleFontOptions = appliedGoogleFonts.map(({ label, value }) => ({
        label: label as string,
        value: value as string,
      }));

      // add google fonts to map using label as key
      googleFontOptions.forEach((font) => {
        fontOptionsMap.set(font.label, font);
      });
    }

    // convert map values back to array and sort alphabetically by label
    return Array.from(fontOptionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const colorOptions = useMemo(() => {
    return [
      ...(getValues("colors.primary")
        ? [
            {
              label: "Brand/Primary",
              value: "primary",
              icon: <ColorSwatch color={primary} />,
              isAlertShown: getIsAlertIconShown({ color: primary, colorName: "primary" }),
            },
          ]
        : []),
      ...(getValues("colors.secondary")
        ? [
            {
              label: "Brand/Secondary",
              value: "secondary",
              icon: <ColorSwatch color={secondary} />,
              isAlertShown: getIsAlertIconShown({ color: secondary, colorName: "secondary" }),
            },
          ]
        : []),
      ...(getValues("colors.tertiary")
        ? [
            {
              label: "Brand/Tertiary",
              value: "tertiary",
              icon: <ColorSwatch color={tertiary} />,
              isAlertShown: getIsAlertIconShown({ color: tertiary, colorName: "tertiary" }),
            },
          ]
        : []),
      {
        label: "Neutrals/Intense",
        value: "intense",
        icon: <ColorSwatch color={intense} />,
        isAlertShown: getIsAlertIconShown({ color: intense, colorName: "intense" }),
      },
      {
        label: "Neutrals/Bold",
        value: "bold",
        icon: <ColorSwatch color={bold} />,
        isAlertShown: getIsAlertIconShown({ color: bold, colorName: "bold" }),
      },
      {
        label: "Neutrals/Soft",
        value: "soft",
        icon: <ColorSwatch color={soft} />,
        isAlertShown: getIsAlertIconShown({ color: soft, colorName: "soft" }),
      },
      {
        label: "Neutrals/Subtle",
        value: "subtle",
        icon: <ColorSwatch color={subtle} />,
        isAlertShown: getIsAlertIconShown({ color: subtle, colorName: "subtle" }),
      },
      {
        label: "Neutrals/White",
        value: "white",
        icon: <ColorSwatch color={white} />,
        isAlertShown: getIsAlertIconShown({ color: white, colorName: "white" }),
      },
      {
        label: "Custom...",
        value: "custom",
        icon: <Icons.ChooseColor className="w-5 h-5 text-neutrals-200 fill-white" />,
      },
    ];
    // eslint-disable-next-line
  }, [getValues, primary]);

  return useMemo(
    () => ({
      fontFamily: formatFontOptions(),
      fallbackFontFamily: FALLBACK_FONT_FAMILY_OPTIONS,
      fontSize: [
        { label: "12px", value: "12px" },
        { label: "14px", value: "14px" },
        { label: "16px", value: "16px" },
        { label: "18px", value: "18px" },
        { label: "20px", value: "20px" },
        { label: "24px", value: "24px" },
        { label: "30px", value: "30px" },
        { label: "36px", value: "36px" },
        { label: "48px", value: "48px" },
        { label: "60px", value: "60px" },
        { label: "72px", value: "72px" },
        { label: "96px", value: "96px" },
        { label: "128px", value: "128px" },
      ],
      lineHeight: [
        { label: "1.2", value: "1.2" },
        { label: "1.5", value: "1.5" },
        { label: "1.8", value: "1.8" },
        { label: "2.0", value: "2.0" },
      ],
      fontWeight: currentFontWeights,
      textColor: colorOptions,
      customColor: [],
      linkColor: colorOptions,
      customLinkColor: [],
    }),
    // eslint-disable-next-line
    [
      primary,
      secondary,
      tertiary,
      intense,
      bold,
      soft,
      subtle,
      white,
      errors,
      googleFonts,
      adobeFontsList,
      currentFontWeights,
    ],
  );
};
